"use client";

import React from "react";
import { Skeleton } from "@/components/ui/adapters";
import {
  ChatRoomHeaderSkeleton,
  ConversationsSkeleton,
  FilterSkeleton,
} from "~/src/secure-chat/Components/Skeleton/Conversations";
import { RoomDetailSkeleton } from "~/src/secure-chat/Components/Skeleton/MessagesPanel";

function DesktopNavigationSkeleton(): React.ReactElement {
  return (
    <div
      role="status"
      aria-label="Loading navigation"
      className="fixed inset-x-0 top-0 z-40 hidden h-[72px] items-center gap-4 bg-white px-8 shadow-common xl:flex print:hidden [&_.animate-pulse]:animate-none"
    >
      <Skeleton className="h-8 w-[136px] shrink-0 rounded-xl" />
      <div className="flex min-w-0 flex-1 items-center justify-end gap-4">
        <Skeleton className="h-10 w-full max-w-[440px] rounded-xl" />
        <Skeleton className="size-10 shrink-0 rounded-xl" />
        <Skeleton className="h-12 w-[148px] shrink-0 rounded-xl" />
      </div>
    </div>
  );
}

export function SecureChatListPageSkeleton(): React.ReactElement {
  return (
    <div
      aria-label="Loading conversations"
      className="flex h-dvh w-full flex-col overflow-hidden bg-background-light xl:bg-secondary-light"
      role="status"
    >
      <DesktopNavigationSkeleton />
      <div className="min-h-0 flex-1 overflow-hidden pb-[calc(4.5rem+env(safe-area-inset-bottom))] xl:hidden">
        <div className="flex h-full w-full flex-col gap-4 overflow-hidden bg-background-light">
          <ChatRoomHeaderSkeleton />
          <FilterSkeleton />
          <div className="min-h-0 flex-1 overflow-hidden">
            <ConversationsSkeleton isBorder />
          </div>
        </div>
      </div>
      <div className="hidden min-h-0 flex-1 pt-[72px] xl:flex">
        <div className="flex size-full min-w-0 gap-4 overflow-hidden p-6">
          <div
            className="flex size-full min-w-[300px] max-w-[400px] flex-col gap-4 overflow-hidden rounded-xl border border-secondary-light bg-background-light 2xl:min-w-[400px]"
            data-testid="secure-chat-desktop-sidebar"
          >
            <ChatRoomHeaderSkeleton />
            <FilterSkeleton />
            <div className="min-h-0 flex-1 overflow-hidden">
              <ConversationsSkeleton isBorder />
            </div>
          </div>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-[calc(0.5rem+env(safe-area-inset-bottom))] flex h-16 items-center gap-2 px-3 xl:hidden">
        <div className="flex h-16 min-w-0 flex-1 items-center justify-around rounded-[2rem] border border-secondary-light bg-white px-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton className="size-9 rounded-xl" key={index} />
          ))}
        </div>
        <Skeleton className="size-14 rounded-full" />
      </div>
    </div>
  );
}

export function SecureChatRoomPageSkeleton(): React.ReactElement {
  return (
    <div
      aria-label="Loading conversation"
      className="size-full min-h-0 min-w-0 overflow-hidden"
      role="status"
    >
      <RoomDetailSkeleton />
    </div>
  );
}
